/**
 * File: button_create_article.tsx
 * Description: Admin button component that triggers the creation of a new FAQ article.
 * Renders a "Crear artículo" button that opens a confirmation dialog showing a markdown
 * preview of the article before submitting it via onCreateArticle. On failure, an error
 * dialog is shown with the caught error message.
 */
import { useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import {
  Button,
  Typography,
  Box,
  useTheme
} from '@mui/material';
import { useThemeModeContext } from '@/context/theme/theme_mode_context';
import type { CategorySelection } from '@/interfaces/category';
import ConfirmDialog from '@/components/admin/confirm_dialog';
import ErrorDialog from '@/components/admin/error_dialog';
import { ColorsPalletTheme } from "@/constants/colors_pallet_theme";

/**
 * Props for the ButtonCreateArticle component.
 *
 * @property {CategorySelection | null} categorySelection - The selected category for the new article; null when no category has been chosen.
 * @property {string} newArticleTitle - The title of the article to be created.
 * @property {string} newArticleContent - The markdown content of the article answer.
 * @property {(categoryId: number | null, categoryTitle: string, articleTitle: string, articleContent: string) => Promise<void>} onCreateArticle - Creates the article. Owned by the page; must reject on failure so this component can surface the error dialog.
 * @property {() => void} onSuccess - Callback invoked after the article is successfully created.
 */
interface ButtonCreateArticleProps {
  categorySelection: CategorySelection | null;
  newArticleTitle: string;
  newArticleContent: string;
  onCreateArticle: (
    categoryId: number | null,
    categoryTitle: string,
    articleTitle: string,
    articleContent: string,
  ) => Promise<void>;
  onSuccess: () => void;
}

/**
 * Button that opens a confirmation dialog before delegating creation of a new FAQ article
 * to onCreateArticle. Disabled until a category, title, and content are all provided. After
 * the user confirms, it submits via onCreateArticle and calls onSuccess; on failure it shows
 * an error dialog.
 *
 * @param {CategorySelection | null} props.categorySelection - The selected category; button is disabled when null.
 * @param {string} props.newArticleTitle - Title text for the new article.
 * @param {string} props.newArticleContent - Markdown body for the new article answer.
 * @param {(categoryId: number | null, categoryTitle: string, articleTitle: string, articleContent: string) => Promise<void>} props.onCreateArticle - Creates the article; must reject on failure.
 * @param {() => void} props.onSuccess - Called after a successful submission.
 * @returns {JSX.Element}
 */
function ButtonCreateArticle({
  categorySelection,
  newArticleTitle,
  newArticleContent,
  onCreateArticle,
  onSuccess,
}: ButtonCreateArticleProps) {

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Main App Theme
  const theme = useTheme();
  const colors = ColorsPalletTheme(theme.palette.mode);
  const { mode } = useThemeModeContext();

  const isDisabled =
    categorySelection === null ||
    newArticleTitle.trim() === '' ||
    newArticleContent.trim() === '';

  /**
   * Delegates creation of the new article to onCreateArticle and handles the response.
   * Closes the confirmation dialog on both success and failure.
   * On success, triggers the onSuccess callback; on failure, displays an error dialog.
   *
   * @returns {Promise<void>}
   * @throws {Error} Caught internally — surfaces via the error dialog rather than propagating.
   */
  async function handleConfirm() {
    if (!categorySelection) {
      return;
    }
    else {
      setIsSubmitting(true);
      try {
        await onCreateArticle(
          categorySelection.categoryId,
          categorySelection.categoryTitle,
          newArticleTitle,
          newArticleContent,
        );
        setConfirmDialogOpen(false);
        onSuccess();
      } catch (error) {
        setConfirmDialogOpen(false);
        setErrorMessage(error instanceof Error ? error.message : 'Error desconocido');
        setErrorDialogOpen(true);
      } finally {
        setIsSubmitting(false);
      }

    }
  }

  return (
    <>
      <Button
        variant="contained"
        disabled={isDisabled}
        onClick={() => { setConfirmDialogOpen(true) }}
      >
        Crear artículo
      </Button>

      <ConfirmDialog
        open={confirmDialogOpen}
        title="Confirmar creación de artículo"
        onClose={() => { setConfirmDialogOpen(false); }}
        onConfirm={() => { void handleConfirm(); }}
        isSubmitting={isSubmitting}
        confirmSx={{
          backgroundColor: colors.greenAccent[500],
            color: 'white',
            "&:hover": {
              background: colors.greenAccent[400],
            }
          }}
      >
        <Typography><strong>Categoría:</strong> {categorySelection?.categoryTitle}</Typography>
        <Typography sx={{ mt: 1 }}><strong>Título:</strong> {newArticleTitle}</Typography>
        <Typography sx={{ mt: 1 }}><strong>Contenido:</strong></Typography>
        <Box sx={{ mt: 1, p:1, border: 2, borderColor: 'primary.main'}} data-color-mode={mode}>
          <MDEditor.Markdown source={newArticleContent} />
        </Box>
      </ConfirmDialog>

      <ErrorDialog
        open={errorDialogOpen}
        title="Error al crear el artículo"
        message={errorMessage}
        onClose={() => { setErrorDialogOpen(false); }}
      />
    </>
  );
}

export default ButtonCreateArticle;
